import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, RefreshCw, Target, Info,
} from "lucide-react";

function SortableTh({ label, k, align = "right", sticky, bordered, colorClass,
                      hint, sortKey, sortDir, onSort }) {
  const active = sortKey === k;
  const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "";
  const cls = [
    "py-2 px-3 whitespace-nowrap cursor-pointer select-none",
    align === "left" ? "text-left" : align === "center" ? "text-center" : "text-right",
    sticky ? "sticky left-0 bg-zinc-50 min-w-[260px] z-10" : "",
    bordered ? "border-l border-zinc-200" : "",
    active ? "text-sky-700" : "hover:text-zinc-800",
    colorClass || "",
  ].filter(Boolean).join(" ");
  const title = hint
    ? `${hint}\n\nClick to sort · click again to reverse`
    : "Click to sort · click again to reverse";
  return (
    <th onClick={() => onSort(k)} className={cls}
      title={title}
      data-testid={`overlap-sort-${k}`}>
      {label} <span className="text-[9px] ml-0.5">{arrow}</span>
    </th>
  );
}


/**
 * AhrefsKeywordOverlapPage — dedicated full-window view of the
 * "Keywords by domain" side-by-side overlap. Optimised for maximum
 * horizontal + vertical real estate so admins can scan hundreds of
 * keywords across many competitors at once.
 *
 * The page:
 *   • Loads the cached `keyword_overlap` snapshot from the assignment
 *     doc — no extra AHREFS units are burned unless the admin clicks
 *     "Re-fetch".
 *   • Adds a live word-search box that filters the visible list as
 *     the user types (substring, case-insensitive).
 *   • Makes every column header clickable to re-sort — keyword (a→z),
 *     volume / KD / CPC, and every per-domain position column so the
 *     admin can immediately see "who's winning what".
 *   • No pagination — the whole overlap renders in a virtual-scroll
 *     table so admins never have to click "show more".
 */
export default function AhrefsKeywordOverlapPage() {
  const { clientId, assignmentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [q, setQ] = useState("");
  const [missingOnly, setMissingOnly] = useState(false);
  const [sortKey, setSortKey] = useState("__default");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/clients/${clientId}/ahrefs/assignments`);
        const a = (data.assignments || []).find(
          (x) => x.assignment_id === assignmentId);
        setAssignment(a || null);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load assignment");
      } finally { setLoading(false); }
    })();
  }, [clientId, assignmentId]);

  const overlap = assignment?.keyword_overlap;
  const target = overlap?.target;
  const domains = overlap?.domains || [];
  const allKeywords = useMemo(() => overlap?.keywords || [], [overlap]);

  const refetch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/clients/${clientId}/ahrefs/assignments/${assignmentId}/keyword-overlap`,
        { sample_limit: 500 });
      setAssignment((prev) => ({ ...(prev || {}), keyword_overlap: data.overlap }));
      const n = data.overlap?.total_keywords || 0;
      toast.success(`Refreshed · ${n.toLocaleString()} unique keywords across ${data.overlap?.domains?.length || 0} domains`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setBusy(false); }
  };

  const cycleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir(key === "keyword" ? "asc" : "desc");
      return;
    }
    if (sortDir === "desc") { setSortDir("asc"); return; }
    if (sortDir === "asc")  { setSortDir("desc"); return; }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return allKeywords.filter((r) => {
      if (needle && !r.keyword.toLowerCase().includes(needle)) return false;
      if (missingOnly) {
        const t = (r.domains || {})[target];
        if (t && t.position != null) return false;
      }
      return true;
    });
  }, [allKeywords, q, missingOnly, target]);

  const valueFor = (row, key) => {
    if (key === "keyword") return (row.keyword || "").toLowerCase();
    if (key === "volume") return row.volume ?? -1;
    if (key === "difficulty") return row.difficulty ?? -1;
    if (key === "cpc") return row.cpc ?? -1;
    if (key.startsWith("dom:")) {
      const dom = key.slice(4);
      return ((row.domains || {})[dom] || {}).position ?? 101;
    }
    return 0;
  };

  const sorted = useMemo(() => {
    if (sortKey === "__default") return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = valueFor(a, sortKey);
      const bv = valueFor(b, sortKey);
      if (typeof av === "string")
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? (av - bv) : (bv - av);
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const fmt = (v) => v == null ? "—" : Number(v).toLocaleString();

  const posPill = (pos) => {
    if (pos == null) return <span className="text-zinc-300">—</span>;
    const cls = pos <= 3 ? "bg-emerald-600 text-white"
      : pos <= 10 ? "bg-amber-500 text-white"
      : "bg-zinc-200 text-zinc-700";
    return <Badge className={`rounded-sm text-[9px] font-mono ${cls}`}>#{pos}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading overlap…
      </div>
    );
  }
  if (!assignment) {
    return (
      <div className="p-8 text-zinc-600">
        <Link to={`/clients/${clientId}/ahrefs-feeds`}
          className="text-sky-700 hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to AHREFS feeds
        </Link>
        <p className="mt-4">Assignment not found.</p>
      </div>
    );
  }
  if (!overlap) {
    return (
      <div className="p-8 text-zinc-600 space-y-4">
        <Link to={`/clients/${clientId}/ahrefs-feeds`}
          className="text-sky-700 hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to AHREFS feeds
        </Link>
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200
          text-amber-900 text-sm rounded-sm p-3">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            No keyword overlap cached for this assignment yet.
            <br />
            Head back to the AHREFS feeds page and click <strong>Keyword overlap</strong>
            {" "}on this assignment to run the first fetch.
          </div>
        </div>
      </div>
    );
  }

  const sortProps = { sortKey, sortDir, onSort: cycleSort };

  return (
    <div className="h-screen w-screen bg-zinc-50 flex flex-col overflow-hidden"
      data-testid="keyword-overlap-page">
      {/* Sticky header — search + filters + controls */}
      <header className="bg-white border-b border-zinc-200 px-4 py-3
        sticky top-0 z-20">
        <div className="flex items-center gap-3 flex-wrap">
          <Link to={`/clients/${clientId}/ahrefs-feeds`}
            className="text-zinc-500 hover:text-sky-700 inline-flex items-center gap-1 text-sm"
            data-testid="back-to-feeds">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <div className="border-l border-zinc-200 h-6" />
          <Target className="h-4 w-4 text-emerald-700" />
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-900">
              Keyword overlap · {allKeywords.length.toLocaleString()} keywords
              × {domains.length} domains
            </div>
            <div className="text-[10px] text-zinc-500">
              {assignment.business_unit_name || assignment.client_id} · target{" "}
              <strong>{target}</strong> · snapshot{" "}
              {new Date(overlap.captured_at).toLocaleString()}
              {overlap.captured_by ? ` · by ${overlap.captured_by}` : ""}
              <span className="ml-2 text-zinc-400">
                · Hover any cell for details ·
                <Badge className="rounded-sm text-[8px] font-mono
                  bg-emerald-600 text-white mx-1">#1</Badge> Top 3
                <Badge className="rounded-sm text-[8px] font-mono
                  bg-amber-500 text-white mx-1">#5</Badge> Page 1
                <Badge className="rounded-sm text-[8px] font-mono
                  bg-zinc-200 text-zinc-700 mx-1">#15</Badge> Elsewhere
                · <strong>tf</strong> = est. organic traffic/month
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <Input placeholder="Search keywords… (live filter)"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="h-8 rounded-sm text-sm w-72"
              data-testid="overlap-search" autoFocus />
            <label className="flex items-center gap-1.5 text-xs text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={missingOnly}
                onChange={(e) => setMissingOnly(e.target.checked)}
                data-testid="overlap-missing-only" />
              Only keywords <strong>{target}</strong> is missing
            </label>
            <Button size="sm" variant="outline" onClick={refetch} disabled={busy}
              className="h-8 rounded-sm text-xs"
              data-testid="overlap-refetch">
              {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    : <RefreshCw className="h-3 w-3 mr-1" />}
              Re-fetch
            </Button>
            <div className="text-xs font-mono text-zinc-500 whitespace-nowrap"
              data-testid="overlap-count">
              {sorted.length.toLocaleString()} of {allKeywords.length.toLocaleString()}
            </div>
          </div>
        </div>
      </header>

      {/* Scrollable table area — force BOTH scrollbars visible via
          `overflow-scroll` so admins always see how much table remains
          off-screen. `min-h-0` on the flex child keeps the vertical
          bar working under the sticky header. */}
      <div className="flex-1 min-h-0 overflow-scroll bg-white
        [scrollbar-gutter:stable]">
        <table className="text-[12px] border-collapse"
          style={{ minWidth: "max-content" }}
          data-testid="overlap-table">
          <thead className="sticky top-0 bg-zinc-50 z-20">
            <tr className="border-b border-zinc-200
              text-[10px] uppercase tracking-wider font-mono text-zinc-500">
              <SortableTh {...sortProps} label="Keyword" k="keyword" align="left" sticky
                hint="The search query users type into Google. Sort A→Z alphabetically." />
              <SortableTh {...sortProps} label="Vol" k="volume"
                hint="Monthly search volume — the estimated number of times this keyword is searched per month in the target country. Higher = more potential traffic." />
              <SortableTh {...sortProps} label="KD" k="difficulty"
                hint="Keyword Difficulty (0-100) — AHREFS' estimate of how hard it is to rank in Google's Top 10 for this keyword. Higher = harder. 0-10 easy, 11-30 medium, 31-70 hard, 71-100 super hard." />
              <SortableTh {...sortProps} label="CPC" k="cpc"
                hint="Cost per click (USD) — the average amount an advertiser pays Google Ads per click for this keyword. Signal of commercial intent: higher CPC = more valuable buying-intent traffic." />
              {domains.map((d) => (
                <SortableTh {...sortProps} key={d} k={`dom:${d}`} align="center" bordered
                  colorClass={d === target ? "text-emerald-900" : "text-zinc-600"}
                  hint={d === target
                    ? `Your site (${d}) — the pill shows YOUR Google position for the keyword and the "tf" number is the estimated organic traffic this keyword sends you each month.`
                    : `Competitor ${d} — the pill shows THEIR Google position for the keyword and the "tf" number is the estimated organic traffic they get from this keyword. Empty (—) means they don't rank in their top 500 keywords.`}
                  label={<>
                    {d === target && (
                      <Badge className="rounded-sm text-[8px] font-mono
                        bg-emerald-600 text-white mr-1">YOU</Badge>
                    )}
                    {d}
                  </>} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={4 + domains.length}
                  className="text-center italic text-zinc-500 py-12 text-sm">
                  No keywords match the current filters.
                </td>
              </tr>
            ) : sorted.map((row, i) => (
              <tr key={row.keyword || `kw-${i}`}
                className="border-b border-zinc-100 last:border-b-0
                  hover:bg-emerald-50/50 whitespace-nowrap"
                data-testid={`overlap-row-${i}`}>
                <td className="py-1 px-3 font-mono sticky left-0 bg-white
                  border-r border-zinc-200 whitespace-nowrap
                  hover:bg-emerald-50/50 z-10"
                  title={`Search query: "${row.keyword}"`}>
                  {row.keyword}
                </td>
                <td className="py-1 px-3 text-right font-mono whitespace-nowrap"
                  title={row.volume == null
                    ? "Monthly search volume unavailable"
                    : `Monthly search volume: ${Number(row.volume).toLocaleString()} searches/month`}>
                  {fmt(row.volume)}
                </td>
                <td className="py-1 px-3 text-right font-mono whitespace-nowrap"
                  title={row.difficulty == null
                    ? "Keyword difficulty unavailable"
                    : `Keyword Difficulty: ${Math.round(row.difficulty)}/100 — ${
                        row.difficulty <= 10 ? "Easy" :
                        row.difficulty <= 30 ? "Medium" :
                        row.difficulty <= 70 ? "Hard" : "Super Hard"
                      }`}>
                  {row.difficulty == null ? "—" : Math.round(row.difficulty)}
                </td>
                <td className="py-1 px-3 text-right font-mono whitespace-nowrap"
                  title={row.cpc == null
                    ? "Cost-per-click unavailable"
                    : `Google Ads cost-per-click: $${Number(row.cpc).toFixed(2)} — advertisers pay this on average per click`}>
                  {row.cpc == null ? "—" : `$${Number(row.cpc).toFixed(2)}`}
                </td>
                {domains.map((d) => {
                  const cell = (row.domains || {})[d] || {};
                  const isMe = d === target;
                  const who = isMe ? "You" : d;
                  const posTip = cell.position == null
                    ? `${who} — does NOT rank in the top 500 for "${row.keyword}"`
                    : `${who} — ranks #${cell.position} on Google for "${row.keyword}"${
                        cell.position <= 3 ? " · Top 3 · money band" :
                        cell.position <= 10 ? " · Page 1" :
                        cell.position <= 20 ? " · Page 2" : " · Page 3+"
                      }`;
                  const trafTip = cell.traffic
                    ? `Estimated organic traffic ${isMe ? "you get" : `${d} gets`} from this keyword: ~${
                        Math.round(cell.traffic).toLocaleString()} visits/month`
                    : "";
                  return (
                    <td key={d} className="py-1 px-3 text-center whitespace-nowrap
                      border-l border-zinc-100">
                      <div className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <span title={posTip}>{posPill(cell.position)}</span>
                        {cell.traffic ? (
                          <span className="text-[10px] font-mono text-zinc-500"
                            title={trafTip}>
                            {fmt(Math.round(cell.traffic))}tf
                          </span>
                        ) : null}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
